import { useMemo } from 'react';
import { CARDINALITY_OPTIONS, externalEntityTypes } from '../constants';
import { Relationship } from '../models/EditableList';
import EditableListTab from './EditableListTab';
import type { EditableListConfig } from '../models/EditableList';
import { useFulfiller } from '../FulfillerContext';
import { useParams } from 'react-router-dom';

interface RelationshipsTabProps {
  relationships: Relationship[];
  onChange: (relationships: Relationship[]) => void;
  readOnlyIds?: string[];
}

const RelationshipsTab = ({
  relationships,
  onChange,
  readOnlyIds,
}: RelationshipsTabProps) => {
  const { entityTypeOptions, isLoadingEntityTypes } = useFulfiller();
  const { entityTypeId } = useParams();
  const filteredEntityTypes = entityTypeId
    ? entityTypeOptions.filter((option) => option.value !== entityTypeId)
    : entityTypeOptions;

  const filteredOptions = [...filteredEntityTypes, ...externalEntityTypes];
  const relationshipsConfig: EditableListConfig = useMemo(() => {
    const allEntityTypes = [...entityTypeOptions, ...externalEntityTypes];
    const excludedIds = new Set<string>();

    if (entityTypeId) {
      excludedIds.add(entityTypeId);
    }

    relationships.forEach((rel) => {
      excludedIds.add(rel.entityTypeId);
    });

    const filteredOptions = allEntityTypes.filter(
      (option) => !excludedIds.has(option.value),
    );

    return {
      itemName: 'Relationship',
      itemNamePlural: 'Relationships',
      primaryField: {
        name: 'entityTypeId',
        label: 'Entity Type Name',
        type: 'select',
        options: filteredOptions,
        placeholder: isLoadingEntityTypes
          ? 'Loading entity types...'
          : 'Entity Types',
        required: true,
      },
      secondaryField: {
        name: 'cardinality',
        label: 'Cardinality',
        type: 'select',
        options: CARDINALITY_OPTIONS,
        placeholder: 'Cardinality',
        required: true,
      },
      stylePrefix: 'relationship',
    };
  }, [
    entityTypeOptions,
    externalEntityTypes,
    entityTypeId,
    relationships,
    isLoadingEntityTypes,
  ]);

  const createRelationship = (
    id: string,
    entityTypeId: string,
    cardinality: string,
  ): Relationship => ({
    id,
    entityTypeId,
    cardinality,
  });

  const updateRelationship = (
    item: Relationship,
    entityTypeId: string,
    cardinality: string,
  ): Relationship => ({
    ...item,
    entityTypeId,
    cardinality,
  });

  return (
    <EditableListTab
      items={relationships}
      config={relationshipsConfig}
      getPrimaryValue={(item) => item.entityTypeId}
      getSecondaryValue={(item) => item.cardinality}
      onChange={onChange}
      createItem={createRelationship}
      updateItem={updateRelationship}
      fullPrimaryOptions={filteredOptions}
      readOnlyIds={readOnlyIds}
    />
  );
};

export default RelationshipsTab;
